export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  image: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

/**
 * The nine services EC Design and Construction LLC offers.
 *
 * No per-service pricing was supplied by the client, so every card carries a
 * "Free Estimate" badge instead of an invented figure.
 */
export const services: ServiceData[] = [
  {
    slug: "home-remodeling",
    title: "Home Remodeling",
    subtitle: "Kitchens, bathrooms, basements & full interiors",
    price: "Free Estimate",
    image: "/assets/images/service-home-remodeling.jpg",
    description:
      "Remodeling is where most of our work starts. Kitchens, bathrooms, basements, and whole-floor interiors — we handle demolition, framing, drywall, flooring, cabinetry, trim, and finish work so you deal with one crew from start to finish instead of chasing separate trades. Every project in Clifton and the surrounding Passaic County towns starts with a free walkthrough and a written estimate.",
    features: [
      "Kitchen and bathroom remodels",
      "Basement and attic finishing",
      "Demolition, framing and drywall",
      "Flooring, tile and trim carpentry",
      "Cabinet and countertop installation",
      "One crew from demo through finish work",
    ],
    process: [
      { step: "Walkthrough", desc: "We visit the property, measure, and talk through what you want changed." },
      { step: "Written Estimate", desc: "You get a clear scope and price before any work is scheduled." },
      { step: "Build", desc: "Our crew works the project in stages and keeps the site clean as we go." },
      { step: "Final Walkthrough", desc: "We review the finished work with you and handle any punch-list items." },
    ],
  },
  {
    slug: "painting",
    title: "Painting",
    subtitle: "Interior & exterior painting done right",
    price: "Free Estimate",
    image: "/assets/images/service-painting.jpg",
    description:
      "A paint job is only as good as the prep underneath it. We patch, sand, caulk, and prime before a finish coat ever goes on, then mask and protect everything that isn't being painted. Interior repaints, full-house exteriors, ceilings, trim, doors, and fresh color on new drywall — for homes and small commercial spaces across northern New Jersey.",
    features: [
      "Interior repaints, room by room or whole house",
      "Exterior siding, trim and doors",
      "Drywall patching, sanding and priming",
      "Ceilings, crown molding and baseboards",
      "Careful masking and daily site cleanup",
      "Color guidance if you want a second opinion",
    ],
    process: [
      { step: "Scope & Colors", desc: "We measure the space and confirm surfaces, colors, and finishes." },
      { step: "Prep Work", desc: "Patching, sanding, caulking, masking and priming — before any color." },
      { step: "Paint", desc: "Coats applied and back-rolled properly, with drying time respected." },
      { step: "Inspection", desc: "We walk the job with you in good light and touch up anything you flag." },
    ],
  },
  {
    slug: "masonry",
    title: "Masonry",
    subtitle: "Brick, block, stone & concrete work",
    price: "Free Estimate",
    image: "/assets/images/service-masonry.jpg",
    description:
      "Masonry is structural work, and it shows when it's done poorly. We build and repair brick, block, and stone — steps, stoops, chimneys, foundations, veneer, and concrete flatwork — with proper footings, drainage, and mortar for New Jersey's freeze-and-thaw winters. Repairs and repointing are just as welcome as new builds.",
    features: [
      "Brick, block and natural stone work",
      "Steps, stoops, porches and walkways",
      "Chimney repair and repointing",
      "Concrete footings, slabs and flatwork",
      "Stone and brick veneer",
      "Built for freeze-thaw durability",
    ],
    process: [
      { step: "Site Review", desc: "We look at grade, drainage, and existing conditions before quoting." },
      { step: "Estimate", desc: "Materials, dimensions and price laid out in writing." },
      { step: "Base & Build", desc: "Proper footings and base prep first, then the masonry itself." },
      { step: "Clean Finish", desc: "Joints tooled, surfaces washed down, and the site cleared." },
    ],
  },
  {
    slug: "roofing",
    title: "Roofing",
    subtitle: "Replacements, repairs & leak work",
    price: "Free Estimate",
    image: "/assets/images/service-roofing.jpg",
    description:
      "Roofs fail slowly and then all at once. We handle full shingle replacements, tear-offs, targeted repairs, flashing and vent work, and leak diagnosis on homes throughout Clifton, Passaic, Paterson, and the surrounding towns. If a repair will genuinely buy you years, we'll say so instead of pushing a full replacement.",
    features: [
      "Full roof replacement and tear-off",
      "Shingle repair and storm damage patching",
      "Flashing, valley and vent work",
      "Leak diagnosis and follow-up",
      "Underlayment and ice-and-water shield",
      "Debris haul-away and magnet sweep",
    ],
    process: [
      { step: "Roof Inspection", desc: "We get on the roof, check the decking, flashing, and problem areas." },
      { step: "Honest Options", desc: "Repair or replace — we tell you which one the roof actually needs." },
      { step: "Install", desc: "Tear-off, deck check, underlayment, then shingles and flashing details." },
      { step: "Cleanup", desc: "Debris removed and the property swept for nails before we leave." },
    ],
  },
  {
    slug: "retaining-walls",
    title: "Retaining Walls",
    subtitle: "Engineered walls that hold their grade",
    price: "Free Estimate",
    image: "/assets/images/service-retaining-walls.jpg",
    description:
      "A retaining wall lives or dies on what's behind it. We build block, stone, and poured walls with a compacted base, drainage stone, and proper backfill so the wall handles water pressure instead of bulging or leaning a few winters in. Ideal for sloped yards, raised patios, driveway edges, and reclaiming usable space on a hill.",
    features: [
      "Segmental block and natural stone walls",
      "Compacted base and drainage stone",
      "Proper backfill and drain pipe",
      "Terraced and multi-level designs",
      "Steps and caps integrated into the wall",
      "Grading and cleanup included",
    ],
    process: [
      { step: "Grade & Drainage", desc: "We assess the slope, soil, and where water actually goes." },
      { step: "Design & Quote", desc: "Wall height, materials, and layout agreed before excavation." },
      { step: "Base First", desc: "Excavation, compacted base, and drainage installed before the first course." },
      { step: "Build & Backfill", desc: "Courses set level, backfilled correctly, then capped and graded." },
    ],
  },
  {
    slug: "pavers",
    title: "Pavers",
    subtitle: "Patios, walkways & driveways",
    price: "Free Estimate",
    image: "/assets/images/service-pavers.jpg",
    description:
      "Paver patios, walkways, and driveways are one of the biggest visible upgrades you can make to a property. We excavate, compact a proper stone base, screed the bedding layer, lay the pattern, then cut, edge, and sand the joints. Done this way a patio stays flat and tight instead of settling into ruts after a couple of seasons.",
    features: [
      "Paver patios and outdoor living areas",
      "Walkways, front steps and landings",
      "Paver and cobble driveways",
      "Excavation and compacted base prep",
      "Border courses, edging and polymeric sand",
      "Pattern and color selection help",
    ],
    process: [
      { step: "Layout", desc: "We mark out the area, confirm the pattern, and check drainage and pitch." },
      { step: "Excavate & Compact", desc: "Dig out, install the stone base, and compact it in lifts." },
      { step: "Lay & Cut", desc: "Bedding sand screeded, pavers set, borders cut in clean." },
      { step: "Edge & Sand", desc: "Edge restraint installed, joints filled and compacted, site cleaned." },
    ],
  },
  {
    slug: "flooring",
    title: "Flooring",
    subtitle: "Hardwood, tile, laminate, vinyl & more",
    price: "Free Estimate",
    image: "/assets/services/flooring.jpg",
    description:
      "A new floor changes how a whole room feels. We install hardwood, engineered wood, laminate, luxury vinyl plank, tile, and carpet for homeowners across Clifton and northern New Jersey. Every job starts with a level subfloor, proper underlayment, and clean transitions before the finished surface goes down.",
    features: [
      "Hardwood and engineered wood installation",
      "Laminate and luxury vinyl plank",
      "Tile flooring for kitchens, baths and mudrooms",
      "Subfloor prep, leveling and repair",
      "Baseboard, shoe molding and transitions",
      "Removal and disposal of old flooring",
    ],
    process: [
      { step: "Measure & Select", desc: "We measure the space, check subfloor condition, and confirm material." },
      { step: "Prep", desc: "Old flooring removed, subfloor leveled, and underlayment installed." },
      { step: "Install", desc: "New flooring laid square and tight, with cuts around walls and fixtures." },
      { step: "Finish", desc: "Transitions, trim and a final cleanup before walkthrough." },
    ],
  },
  {
    slug: "new-construction",
    title: "New Construction",
    subtitle: "Ground-up builds & additions",
    price: "Free Estimate",
    image: "/assets/images/service-new-construction.jpg",
    description:
      "For ground-up projects and large additions we take the build from foundation through finish — site prep and footings, framing, roofing, exterior, and the interior trades that follow. Because we self-perform masonry, framing, roofing, and finish work, fewer hands touch the schedule and fewer things fall between trades.",
    features: [
      "Ground-up residential construction",
      "Additions and structural expansions",
      "Foundations, footings and site prep",
      "Framing, roofing and exterior envelope",
      "Interior build-out and finish work",
      "Coordination of the trades we don't self-perform",
    ],
    process: [
      { step: "Project Review", desc: "We go over plans, scope, and the realistic timeline for the build." },
      { step: "Detailed Estimate", desc: "A written breakdown by phase so you can see where the money goes." },
      { step: "Build Phases", desc: "Foundation, framing, envelope, then interior — with updates as we go." },
      { step: "Handover", desc: "Final walkthrough, punch list, and the property cleared and cleaned." },
    ],
  },
  {
    slug: "general-construction",
    title: "General Construction",
    subtitle: "Repairs, build-outs & the jobs in between",
    price: "Free Estimate",
    image: "/assets/images/service-general-construction.jpg",
    description:
      "Not every job fits neatly into one trade. General construction covers the rest — structural repairs, drywall and carpentry work, small commercial build-outs, exterior fixes, and the punch-list projects other contractors won't take on. If you're not sure which service your project falls under, this is the one to ask about.",
    features: [
      "Structural and water-damage repairs",
      "Drywall, carpentry and trim work",
      "Small commercial and retail build-outs",
      "Exterior repairs and siding work",
      "Doors, windows and openings",
      "Smaller jobs other contractors pass on",
    ],
    process: [
      { step: "Tell Us the Problem", desc: "Describe the job or send photos — we'll tell you if it's in our lane." },
      { step: "On-Site Look", desc: "We come out, diagnose it properly, and quote the actual fix." },
      { step: "Do the Work", desc: "Scheduled around you and completed by the same crew that quoted it." },
      { step: "Check It Together", desc: "We don't call it done until you've seen it and agree it is." },
    ],
  },
];
